import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import SupplierService from "../../../services/supplier/supplier-service";
import { RootState } from "../../store";
import { ApplicationSupplierProductUpload } from "../../../models/supplier/application-supplier-product-upload";
import { ApplicationError } from "../../../models/errors/application-error";
import { ApplicationSupplierProductsList } from "../../../models/supplier/application-supplier-product-list";

interface SupplierProductUploadState {
  products: ApplicationSupplierProductUpload[];
  loading: boolean;
  error: string | null;
}

const initialState: SupplierProductUploadState = {
  products: [],
  loading: false,
  error: null,
};

export const bulkUploadProducts = createAsyncThunk<
  ApplicationSupplierProductsList | null,
  {
    supplierId: number;
    products: ApplicationSupplierProductUpload[];
  },
  { state: RootState }
>("supplierProductUpload/bulkUpload", async (payload, { rejectWithValue }) => {
  const supplierService = new SupplierService();

  try {
    const response = await supplierService.bulkUploadProducts(
      payload.supplierId,
      payload.products
    );
    return response;
  } catch (error) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError || "Failed to upload products");
  }
});

interface UpdateProductFieldPayload {
  name: string;
  field: keyof ApplicationSupplierProductUpload;
  value: any;
}

const supplierProductUploadSlice = createSlice({
  name: "supplierProductUpload",
  initialState,
  reducers: {
    storeProcessedRecords(
      state,
      action: PayloadAction<ApplicationSupplierProductUpload[] | null>
    ) {
      if (action.payload) {
        state.products = action.payload.map((product) => ({
          ...product,
          openPricing: (product.price?.toString()) != '' && product.price == 0,
          price: product.price ? Number(product.price) : undefined,
          size: product.size ? Number(product.size) : undefined,
          sizeunit: product.sizeunit,
          minimumOrderQuantity: product?.minimumOrderQuantity ? Number(product.minimumOrderQuantity) : undefined,
          information: product.information,
          reference: product.reference,
          plu: product.plu,
          origin: product.origin
        }));
      } else {
        state.products = [];
      }
    },
    removeProductByName(state, action: PayloadAction<string>) {
      const index = state.products.findIndex(
        (product) => product.name === action.payload
      );

      if (index !== -1) {
        state.products.splice(index, 1);
      }
    },
    resetProductState(state) {
      state.products = initialState.products;
      state.loading = initialState.loading;
      state.error = initialState.error;
    },
    updateProductField(state, action: PayloadAction<UpdateProductFieldPayload>) {
      const { name, field, value } = action.payload;
      const product = state.products.find((p) => p.name === name);
      if (product) {
        (product as any)[field] = value;
      }
    },
  },
});

export const {
  storeProcessedRecords,
  removeProductByName,
  resetProductState,
  updateProductField
} = supplierProductUploadSlice.actions;
export default supplierProductUploadSlice.reducer;
